import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileDown, Loader2, CheckCircle2 } from "lucide-react";
import { fmtDT } from "../lib/date";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const money = (n) => "$" + Number(n || 0).toLocaleString();

export default function QuoteView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/public/quote/${token}`)
      .then((r) => { if (!r.ok) throw new Error("invalid"); return r.json(); })
      .then(setData)
      .catch(() => setErr(true));
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/quote/${token}/accept`, { method: "POST" });
      const d = await r.json();
      setData((prev) => ({ ...prev, acceptedAt: d.acceptedAt }));
    } catch { /* ignore */ }
    finally { setAccepting(false); }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="quote-invalid">报价单链接无效或已失效</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-500" /></div>;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="quote-view-page">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-brand-500 text-white px-7 py-6">
          <h1 className="text-2xl font-bold">阿里建筑 · 工程报价单</h1>
          <p className="text-white/80 text-sm mt-1">ALIGCUS Construction · Quotation</p>
        </div>
        <div className="px-7 py-5 space-y-1 text-sm border-b border-slate-100">
          <p><span className="text-slate-400 inline-block w-20">工程项目</span><span className="text-slate-800 font-medium">{data.address || "-"}</span></p>
          <p><span className="text-slate-400 inline-block w-20">业主</span><span className="text-slate-800">{data.clientName || "-"}</span></p>
          <p><span className="text-slate-400 inline-block w-20">项目类别</span><span className="text-slate-800">{data.tradeName || "-"}</span></p>
        </div>
        <div className="px-7 py-5">
          <table className="w-full text-sm" data-testid="quote-items-table">
            <thead className="text-slate-400 text-xs border-b border-slate-200">
              <tr>
                <th className="text-left py-2">项目 Item</th>
                <th className="text-right py-2">单价</th>
                <th className="text-right py-2">数量</th>
                <th className="text-right py-2">小计</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data.items || []).map((it, i) => (
                <tr key={i} data-testid={`quote-item-${i}`}>
                  <td className="py-2 text-slate-700">{it.name}<span className="text-slate-400 text-xs ml-1">/ {it.unit}</span></td>
                  <td className="py-2 text-right text-slate-500">${Number(it.price).toLocaleString()}</td>
                  <td className="py-2 text-right text-slate-500">{it.qty}</td>
                  <td className="py-2 text-right font-medium text-slate-800">{money(it.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between mt-4 pt-4 border-t-2 border-slate-800">
            <span className="font-semibold text-slate-700">合计 Total</span>
            <span className="text-2xl font-bold text-red-600" data-testid="quote-total">{money(data.total)}</span>
          </div>
          <div className="flex items-center gap-3 mt-5 flex-wrap">
            <a href={`${BACKEND}/api/public/quote/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="quote-pdf-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white text-sm hover:bg-slate-900">
              <FileDown className="h-4 w-4" />下载报价单 PDF
            </a>
            {data.acceptedAt ? (
              <span data-testid="quote-accepted-badge" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-50 text-green-700 text-sm font-semibold border border-green-200">
                <CheckCircle2 className="h-4 w-4" />您已于 {fmtDT(data.acceptedAt)} 确认此报价
              </span>
            ) : (
              <button data-testid="quote-accept-btn" disabled={accepting} onClick={accept}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-brand-500 text-white text-sm font-semibold hover:bg-brand-600 disabled:opacity-60">
                {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}确认接受报价
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-4">本报价按上述单项数量与单价核算，最终以实际施工量为准；报价有效期 30 天。</p>
        </div>
      </div>
    </div>
  );
}
